import { Component, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { PageService } from "@src/app/servicios/pages/pages.service";
import { Page } from "@src/app/models/interfaces/page.interface";
import { ModalLoginComponent } from "../modal-login/modal-login.component";
import { ModalRegistroComponent } from "../modal-registro/modal-registro.component";
import { NgIf } from "@angular/common";

@Component({
  selector: "app-pages",
  templateUrl: "./pages.component.html",
  styleUrls: ["./pages.component.css"],
  imports: [ModalLoginComponent, ModalRegistroComponent, NgIf],
})
export class PagesComponent implements OnInit, OnDestroy {
  constantesGlobales: any;
  pages: Page;
  rSub: Subscription;
  slug: string;

  constructor(
    private readonly pageService: PageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly platformService: PlatformService,
    private readonly configService: ConfigService,
  ) {
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  /**
   * Método del ciclo de vida de Angular que se ejecuta al inicializar el componente.
   *
   * Este método utiliza `firstValueFrom` para obtener parámetros de la ruta activa (`paramMap`)
   * y determinar el valor del parámetro `slug`. Si el parámetro `slug` está presente,
   * se carga la página correspondiente llamando al método `loadPage`.
   * En caso contrario, se obtienen datos adicionales de la ruta activa (`data`)
   * para cargar la página basada en el valor de `slug` proporcionado en los datos.
   *
   * @returns {void} Este método no devuelve ningún valor.
   */
  ngOnInit(): void {
    firstValueFrom(this.activatedRoute.paramMap).then((paramMap) => {
      this.slug = paramMap.get("slug");

      if (this.slug) {
        this.loadPage(this.slug);
      } else {
        firstValueFrom(this.activatedRoute.data).then((data) => {
          this.loadPage(data.slug);
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.rSub?.unsubscribe();
  }

  /**
   * Carga una página basada en el slug proporcionado y actualiza las propiedades de la clase con los datos obtenidos.
   * Si no se encuentra la página o ocurre un error, redirige al usuario a la página 404.
   *
   * @param slug - El identificador único de la página que se desea cargar.
   * @returns Una promesa que se resuelve cuando la página ha sido cargada y procesada.
   *
   * @throws Redirige a "/404" si no se encuentra la página o si ocurre un error durante la carga.
   */
  private async loadPage(slug: string): Promise<void> {
    try {
      const page: Page = await firstValueFrom(this.pageService.getPages(slug));

      if (!page) {
        this.router.navigateByUrl("/404");
        return;
      }

      this.pages = page;      
      if (this.slug === 'codigo-qr' && this.pages.html) {
        if (typeof this.pages.html === 'string') {
          window.location.href = this.pages.html;
        }
        return;
      }
      this.pages.showLogin = page.showLogin === 1;

      if (this.platformService.isBrowser()) {
        this.pages.prehtml = this.sanitizeHtml(this.pages.prehtml);
        this.pages.html = this.sanitizeHtml(this.pages.html);
      }
    } catch {
      this.router.navigateByUrl("/404");
    }
  }

  private sanitizeHtml(content: any): any {
    return this.isSanitizedHtml(content) ? content : this.sanitizer.bypassSecurityTrustHtml(content as string);
  }

  private isSanitizedHtml(obj: any): boolean {
    return obj?.constructor?.name === "SafeHtmlImpl";
  }
}
